// @ts-strict-ignore

/**
 * @module BubbleConferences
 */

import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { BubbleConferenceRecap, BubbleConferenceRecapFile, BubbleConferenceRecapRB, RecapViewer } from 'rainbow-web-sdk/models/bubbleConferenceRecap.model';
import { FileDescriptor } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { FileStorageService } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { WebConferenceSession } from 'rainbow-web-sdk/models/webConferenceSession.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { LoggerService, SettingsService, XmppService } from 'rainbow-web-sdk/services';
import { User, RBEvent, Bubble, BubbleConference } from 'rainbow-web-sdk/models';
import { Subject, Subscription, filter } from 'rxjs';
import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { BubbleServiceRB } from '../bubbles/bubble.service';
import { stringifyError } from '../../utils/stringifyError';

/** @internal */
export const CONFERENCE_RECAP_SVC = 'ConferenceRecapService';

/**
 * @eventProperty
 * @internal
 * `BubbleConferenceRecapServiceEvents` specific events
 */
export enum BubbleConferenceRecapServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when a new conference recording is ready
     */
    ON_RECAP_CREATED = 'ON_RECAP_CREATED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a new conference recording is ready
     */
    ON_RECAP_READY = 'ON_RECAP_READY',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a conference recording is shared
     */
    ON_RECAP_SHARED = 'ON_RECAP_SHARED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a conference recording is removed
     */
    ON_RECAP_REMOVED = 'ON_RECAP_REMOVED',
}

/**
 * Transcription and summary automatic generation is still an experimental feature.
 * It may be not available on your platform.
 */
export class BubbleConferenceRecapParams {
    public transcription: boolean = false;
    public summary: boolean = false;
    public summaryForm?: string;
    public persistent: boolean = true;
    public inputLanguage: string = 'auto';
    public summaryPrompt?: string;
}

/** @internal */
export interface BubbleConferenceRecapService {

    /**
     * Subscribe to BubbleConferenceRecapService service events
     * @param handler - The handler function that will be called on events
     * @param eventNames - The events to subscribe to
     * @internal
     */
    subscribe(handler: (event: RBEvent<BubbleConferenceRecapServiceEvents>) => any, eventNames?: BubbleConferenceRecapServiceEvents | BubbleConferenceRecapServiceEvents[]): Subscription;

    /**
     * Get all conference recaps that belong to the connected user
     */
    getRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]>;

    /**
     * Get recap from cache
     */
    getRecapFromCacheById(recapId: string): BubbleConferenceRecap;

    /**
     * Get conference recaps shared with the connected user
     */
    getSharedWithMeRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]>;

    /** @internal */
    getSharedWithMeConferenceRecordingFilesOld(bubble?: Bubble): Promise<FileDescriptor[]>;

    /**
     * Delete conference recording file
     * @internal
     * @param conferenceRecordingFileId - ID of the conference recording file (note: not file descriptor ID)
     */
    deleteConferenceRecordingFile(conferenceRecordingFileId: string): Promise<void>;

    /**
     * Get the list of the available custom prompts for the conference recap
     */
    getCustomPromptsForTranscription(): Promise<{ name: string; id: string; }[]>;

    /**
     * Fetch conference recording's associated files from the server.
     * Returns an array of all the files associated to the recording (.mp4, .m4a, .png and .txt)
     * @internal
     * @param conferenceRecordingFile - Conference recording file
     */
    getConferenceRecordingAssociatedFiles(conferenceRecordingFile: BubbleConferenceRecap): Promise<FileDescriptor[]>;
}

/**
 * The module is responsible for recording web conferences.
 * @internal
 */
export class BubbleConferenceRecapServiceRB extends Service implements BubbleConferenceRecapService {

    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private bubbleService: BubbleServiceRB = BubbleServiceRB.getInstance();
    private fileStorageService: FileStorageService = FileStorageService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();

    private getConferenceRecordingFilesPromises: Record<string, Promise<BubbleConferenceRecap[]>> = {};
    private getSharedWithMeConferenceRecordingFilesPromises: Record<string, Promise<BubbleConferenceRecap[]>> = {};
    private getSharedWithMeConferenceRecordingFilesOldPromises: Record<string, Promise<FileDescriptor[]>> = {};
    private eventsSubject: Subject<RBEvent<BubbleConferenceRecapServiceEvents>> = new Subject<RBEvent<BubbleConferenceRecapServiceEvents>>();
    private recaps?: BubbleConferenceRecap[];
    private sharedRecaps?: BubbleConferenceRecap[];
    private sharedRecordingFilesOld?: FileDescriptor[];

    private subscription: Subscription;
    private messageHandlerRef?: any;

    public static getInstance() { return Service.create(CONFERENCE_RECAP_SVC, BubbleConferenceRecapServiceRB); }
    public static build() { return new BubbleConferenceRecapServiceRB(); }
    protected constructor() { super(); }

    public start(): void {
        try {
            this.logger.info(`[BubbleConferenceRecapService] === STARTING`);
            this.attachEventListeners();
            this.logger.info(`[BubbleConferenceRecapService] === STARTED`);
        }
        catch (error) {
            this.logger.warn(`[BubbleConferenceRecapService] === STARTING FAILURE === ${stringifyError(error)}`);
        }
    }

    public stop(): void {
        try {
            this.logger.warn(`[BubbleConferenceRecapService] === STOPPING`);
            this.recaps = undefined;
            this.sharedRecaps = undefined;
            this.subscription?.unsubscribe();
            this.subscription = undefined;
            if (this.messageHandlerRef) {
                this.xmppService.deleteHandler(this.messageHandlerRef);
                this.messageHandlerRef = undefined;
            }
            this.logger.warn(`[BubbleConferenceRecapService] === STOPPED`);
        }
        catch (error) {
            this.logger.warn(`[TranscriptionService] === STOPPING FAILURE === ${error}`);
        }
    }

    public reconnect(): void {
        try {
            this.logger.warn(`[BubbleConferenceRecapService] === RECONNECTING`);
            this.recaps = undefined;
            this.sharedRecaps = undefined;
            this.subscription?.unsubscribe();
            this.subscription = undefined;
            this.attachEventListeners();
            this.logger.warn(`[BubbleConferenceRecapService] === RECONNECTED`);
        }
        catch (error) {
            this.logger.warn(`[BubbleConferenceRecapService] === RECONNECTING FAILURE === ${error}`);
        }
    }

    public subscribe(handler: (event: RBEvent<BubbleConferenceRecapServiceEvents>) => any, eventNames?: BubbleConferenceRecapServiceEvents | BubbleConferenceRecapServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.eventsSubject.pipe(filter((event: RBEvent<BubbleConferenceRecapServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventsSubject;

        return observable.subscribe((event: RBEvent<BubbleConferenceRecapServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[BubbleConferenceRecapService] subscribe () -- error -- ${error.message}`); }
        });
    }

    private sendEvent(name: BubbleConferenceRecapServiceEvents, data: any = null) {
        this.eventsSubject.next(RBEvent.create(name, data));
    }

    private attachEventListeners(): void {
        if (this.messageHandlerRef) { this.xmppService.deleteHandler(this.messageHandlerRef); this.messageHandlerRef = undefined; }
        // @ts-expect-error -- strophe isn't supposed to accept promise and doesn't support it, but be need it to works
        this.messageHandlerRef = this.xmppService.addHandler(async (stanza) => {
            try
            {
                const stanzaElem = xmlParser(stanza);
                const recordingElem = stanzaElem.find('conferencerecordings');
                const statusElem = stanzaElem.find('conferencerecordingstatus');
                /*let recordingElem = stanzaElem.find('conferencerecordings');
                if (recordingElem.length === 0) recordingElem = stanzaElem.find('recordingfile');*/

                if (statusElem.length) {
                    //check for update of isephemeral
                    const action = statusElem.attr('action');
                    const recapId = statusElem.find('recordid').text();
                    const isEphemeral = statusElem.find('isephemeral').text();
                    if (action === 'update' && recapId) {

                        const recapIndex = this.recaps.findIndex(recap => recap.id === recapId);
                        const recap = this.recaps[recapIndex];

                        //get recap and update the new
                        if (isEphemeral === '' || isEphemeral === 'false') {
                            //ephemeral is now removed
                            if (recap) { recap.isEphemeral = false; }
                        }

                        //update viewers
                        const viewersElem = statusElem.find('viewers');
                        if (viewersElem?.length) {
                            //update list of viewers
                            const viewers = viewersElem.find('viewer').map((viewer: any) => {
                                return { type: viewer.attr("type"), viewerId: viewer.text() };
                            });

                            if (recap) { recap.viewers = viewers; }
                        }
                    }
                }

                if (recordingElem.length > 0) {
                    let meetingRecordingStatus: string;
                    let meetingTranscriptionStatus: string;
                    let transcriptionAsked: boolean;
                    let summaryAsked: boolean;
                    let errorDetails: string;
                    let isEphemeral: boolean = false;

                    const action = recordingElem.attr('action');
                    const recapId = recordingElem.attr('id');
                    if (action === 'delete') {
                        const recapIndex = this.recaps.findIndex(recap => recap.id === recapId);
                        const recap = this.recaps[recapIndex];
                        if (recapIndex !== -1) this.recaps.splice(recapIndex, 1);
                        this.recaps.sort((fileA, fileB) => { return new Date(fileB.startDate).getTime() - new Date(fileA.startDate).getTime(); });
                        this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_REMOVED, recap);
                    }
                    else {
                        const purposeElem = recordingElem.find('purpose');
                        if (purposeElem.length > 0) {
                            meetingRecordingStatus = purposeElem.find('meetingrecording').attr('status');
                            meetingTranscriptionStatus = purposeElem.find('transcription').attr('status');
                            transcriptionAsked = Boolean(purposeElem.find('transcriptionreason').attr('transcription') === 'true');
                            summaryAsked = Boolean(purposeElem.find('transcriptionreason').attr('summary') === 'true');
                            errorDetails = purposeElem.find('transcriptionreason').attr('error');
                        }

                        isEphemeral = recordingElem.find('isephemeral').text() === 'true';
                        const timeToLiveDate = recordingElem.find('timetolivedate').text();

                        const data: any = {
                            id: recordingElem.attr('id'),
                            status: recordingElem.find('status').text(),
                            roomId: recordingElem.find('roomid').text(),
                            roomName: recordingElem.find('roomname').text(),
                            roomTopic: recordingElem.find('roomtopic').text(),
                            recordingStartDate: new Date(recordingElem.find('recordingstartdate').text()),
                            overallArchiveSize: Number(recordingElem.find('overallarchivesize').text()),
                            avatarURL: recordingElem.find('avatarurl').text(),
                            userId: recordingElem.find('userid').text() ? recordingElem.find('userid').text() : this.contactService.userContact.dbId,
                            isEphemeral: isEphemeral,
                            timeToLiveDate: timeToLiveDate
                        };
                        const recordsElem = recordingElem.find('records');
                        if (recordsElem.length > 0) {
                            const recordingFiles = recordsElem.find('recordingfile');
                            data.records = recordingFiles.map((recordingFile: any) => {
                                return {
                                    fileId: recordingFile.find('fileid').text(),
                                    fileName: recordingFile.find('filename').text(),
                                    md5sum: recordingFile.find('md5sum').text(),
                                    size: recordingFile.find('size').text(),
                                    typeMIME: recordingFile.find('typemime').text()
                                };
                            });
                        }

                        //get participants if available
                        // const participantsElem = recordingElem.find('paricipants');
                        //Some informations are missing, so we need to put flag to update from server
                        data.isInitialized = false;

                        //get viewers if available
                        const viewersElem = recordingElem.find('viewers');
                        if (viewersElem?.length) {
                            //update list of viewers
                            const viewers = viewersElem.find('viewer').map((viewer: any) => {
                                return { type: viewer.attr("type"), viewerId: viewer.text() };
                            });

                            data.viewers = viewers;
                        }

                        if (meetingRecordingStatus) { data.statusMeetingRecording = meetingRecordingStatus; }
                        if (meetingTranscriptionStatus) { data.statusTranscription = meetingTranscriptionStatus; data.statusSummary = meetingTranscriptionStatus; }
                        if (transcriptionAsked) { data.transcription = transcriptionAsked; }
                        if (summaryAsked) { data.summary = summaryAsked; }
                        if (errorDetails) { data.transcriptionReason = errorDetails; }

                        // Create or update recordingFile
                        if (!this.recaps) this.recaps = [];

                        let recap = this.recaps.find(recordingFile => recordingFile.id === data.id);
                        if (recap) recap.updateFromData(data);
                        else {
                            this.recaps.push(recap = BubbleConferenceRecapRB.createFromData(data));
                            // Send ON_RECAP_CREATED event when changes
                            if (data.status === 'initiated' || data.status === 'in_progress') {
                                this.recaps.sort((fileA, fileB) => { return new Date(fileB.startDate).getTime() - new Date(fileA.startDate).getTime(); });
                                this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_CREATED, recap);
                            }
                        }

                        this.logger.info(`[BubbleConferenceRecapService] onRecapEventReceived ${JSON.stringify(recap.toJSON(), null, 2)}`);

                        // Send ON_RECAP_READY event when release complete
                        if (data.status === 'release_complete') {
                            this.recaps.sort((fileA, fileB) => { return new Date(fileB.startDate).getTime() - new Date(fileA.startDate).getTime(); });
                            this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_READY, recap);
                        }
                        else if (data.status === 'failed') {
                            //recaps failed, but send event nevertheless
                            this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_READY, recap);
                        }
                    }
                }
            }
            catch {}
            return true;
        }, null, "message", "management");

        this.fileStorageService.sharedWithMeConferenceRecordingFilesSubject.subscribe(() => {
            this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_SHARED);
        });
    }

    public async startRecap(bubbleConference: BubbleConference, params?: BubbleConferenceRecapParams): Promise<void> {
        try {
            if (!bubbleConference || !bubbleConference.id) throw Error("Wrong or missing parameters");

            let data = null;
            if (params) {
                data = JSON.parse(JSON.stringify(params));
                if (!this.contactService.userContact.canRecordingTranscription) {
                    data = {
                        persistent: params.persistent
                    };
                } else {
                    if (data?.summaryPrompt?.length > 0) {
                        if (Object.hasOwn(params, "summaryForm"))
                            delete data.summaryForm;
                    } else if (data?.summaryForm?.length > 0) {
                        if (Object.hasOwn(data, "summaryPrompt"))
                            delete data.summaryPrompt;
                    }
                }
            }

            // Check available space
            let consumptionData = this.fileStorageService.getConsumptionData();
            if (!consumptionData) consumptionData = await this.fileStorageService.retrieveUserConsumption();
            if (consumptionData.maxValue - consumptionData.currentValue <= 0) throw Error("Not enough storage space");

            // Start request
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubbleConference.id}/start-recording`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(data ? data : {}) });
            if (!response.ok) throw response;

            (bubbleConference as WebConferenceSession).record.isRecording = true;
            (bubbleConference as WebConferenceSession).record.recordingIsPaused = false;

            this.logger.info("[BubbleConferenceRecapService] startRecap -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startRecap');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public getRecapFromCacheById(recapId: string): BubbleConferenceRecap {
        let result = this.recaps?.find(recap => recap?.id === recapId);
        if (!result) {
            result = this.sharedRecaps?.find(recap => recap?.id === recapId);
        }

        return result;
    }

    public async pauseRecap(bubbleConference: BubbleConference): Promise<void> {
        try {
            if (!bubbleConference || !bubbleConference.id) { throw Error("Wrong or missing parameters"); }

            // Pause request
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubbleConference.id}/pause-recording`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;

            (bubbleConference as WebConferenceSession).record.isRecording = true;
            (bubbleConference as WebConferenceSession).record.recordingIsPaused = true;

            this.logger.info("[BubbleConferenceRecapService] pauseRecap -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'pauseRecap');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async resumeRecap(bubbleConference: BubbleConference): Promise<void> {
        try {
            if (!bubbleConference || !bubbleConference.id) { throw Error("Wrong or missing parameters"); }

            // Resume request
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubbleConference.id}/resume-recording`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;

            (bubbleConference as WebConferenceSession).record.isRecording = true;
            (bubbleConference as WebConferenceSession).record.recordingIsPaused = false;

            this.logger.info("[BubbleConferenceRecapService] resumeRecap -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'resumeRecap');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async stopRecap(bubbleConference: BubbleConference): Promise<void> {
        try {
            if (!bubbleConference || !bubbleConference.id) { throw Error("Wrong or missing parameters"); }

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubbleConference.id}/stop-recording`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;

            (bubbleConference as WebConferenceSession).record.isRecording = false;
            (bubbleConference as WebConferenceSession).record.recordingIsPaused = false;
            (bubbleConference as WebConferenceSession).record.hideBanner = false;

            this.logger.info("[BubbleConferenceRecapService] stopRecap -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'stopRecap');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async deleteRecap(conferenceRecap: BubbleConferenceRecap): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/filestorage/v1.0/conferences-recordings/${conferenceRecap.id}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw response;
            this.logger.info("[BubbleConferenceRecapService] deleteConferenceRecap -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `deleteConferenceRecap(recap: ${conferenceRecap.id})`);
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async deleteConferenceRecordingFile(conferenceRecordingFileId: string): Promise<void> {
        try {
            if (!conferenceRecordingFileId) throw new Error("[BubbleConferenceRecapService] deleteConferenceRecordingFile - ERROR: no conferenceRecordingFileId");

            await this.fileStorageService.deleteConferenceRecordingFile(conferenceRecordingFileId);
            const index = this.recaps.findIndex(recordingFile => recordingFile.id === conferenceRecordingFileId);
            if (index !== -1) this.recaps.splice(index, 1);
            this.logger.info("[BubbleConferenceRecapService] deleteConferenceRecordingFile");
            this.sendEvent(BubbleConferenceRecapServiceEvents.ON_RECAP_REMOVED);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteConferenceRecordingFile');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async updateRecapFromServer(recap: BubbleConferenceRecap): Promise<void> {
        await this.fileStorageService.refreshConferenceRecapFromServcer(recap);
    }

    public async getRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]> {
        const promiseId = bubble ? bubble.dbId : 'all';
        if (this.getConferenceRecordingFilesPromises[promiseId]) return this.getConferenceRecordingFilesPromises[promiseId];
        const promise = (async () => {
            try {
                if (!this.recaps) {
                    this.logger.info(`[BubbleConferenceRecapService] getConferenceRecaps ()`);
                    this.recaps = await this.fileStorageService.getRecordingFilesFromServer();
                }
                await new Promise(res => setTimeout(res, 1));
                return this.filterRecordingFiles(bubble);
            }
            finally { delete this.getConferenceRecordingFilesPromises[promiseId]; }
        })();
        this.getConferenceRecordingFilesPromises[promiseId] = promise;
        return promise;
    }

    public filterRecordingFiles(bubble: Bubble): BubbleConferenceRecap[] {
        if (!bubble) return this.recaps;
        return this.recaps.reduce((confRecFiles: BubbleConferenceRecap[], confRecFile: BubbleConferenceRecap) => {
            //find also the shared recaps in this bubble
            if (confRecFile.bubbleId === bubble.dbId || confRecFile.isSharedWith(bubble)) {
                //check if not already in list
                if (confRecFiles.indexOf(confRecFile) === -1) { confRecFiles.push(confRecFile); }
            }

            return confRecFiles;
        }, []);
    }

    public async getSharedWithMeConferenceRecordingFilesOld(bubble?: Bubble): Promise<FileDescriptor[]> {
        return [];
        // const promiseId = bubble ? bubble.dbId : 'all';
        // if (this.getSharedWithMeConferenceRecordingFilesOldPromises[promiseId]) return this.getSharedWithMeConferenceRecordingFilesOldPromises[promiseId];
        // const promise = (async () => {
        //     try {
        //         if (!this.sharedRecordingFilesOld) {
        //             this.logger.info(`[BubbleConferenceRecapService] getSharedWithMeConferenceRecordingFilesOld ()`);
        //             this.sharedRecordingFilesOld = await this.fileStorageService.getSharedWithMeConferenceRecordingFilesFromServer();
        //         }
        //         await new Promise(res => setTimeout(res, 1));
        //         return this.sharedRecordingFilesOld;
        //     }
        //     finally { delete this.getSharedWithMeConferenceRecordingFilesOldPromises[promiseId]; }
        // })();
        // this.getSharedWithMeConferenceRecordingFilesOldPromises[promiseId] = promise;
        // return promise;
    }

    public async getSharedWithMeRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]> {
        return [];
        // const promiseId = bubble ? bubble.dbId : 'all';
        // if (this.getSharedWithMeConferenceRecordingFilesPromises[promiseId]) return this.getSharedWithMeConferenceRecordingFilesPromises[promiseId];
        // const promise = (async () => {
        //     try {
        //         if (!this.sharedRecaps) {
        //             this.logger.info(`[BubbleConferenceRecapService] getSharedWithMeConferenceRecaps ()`);
        //             const fileDescriptors = await this.fileStorageService.getSharedWithMeConferenceRecordingFilesFromServer();
        //             this.sharedRecaps = this.correlateSharedFiles(fileDescriptors);
        //         }
        //         await new Promise(res => setTimeout(res, 1));
        //         return this.sharedRecaps;
        //     }
        //     finally { delete this.getSharedWithMeConferenceRecordingFilesPromises[promiseId]; }
        // })();
        // this.getSharedWithMeConferenceRecordingFilesPromises[promiseId] = promise;
        // return promise;
    }

    private correlateSharedFiles(fileDescriptors: FileDescriptor[]): BubbleConferenceRecap[] {
        return fileDescriptors.reduce((conferenceRecordingFiles: BubbleConferenceRecap[], fileDescriptor: FileDescriptor) => {
            const conferenceRecordingFileId = fileDescriptor?.tags?.conferenceRecording?.filesConfRecordingId;
            let conferenceRecordingFile = conferenceRecordingFiles.find(conferenceRecordingFile => conferenceRecordingFile.id === conferenceRecordingFileId);
            if (!conferenceRecordingFile) {
                const roomId = fileDescriptor?.tags?.conferenceRecording?.roomId;
                const bubble = this.bubbleService.getBubbleFromCache(roomId);
                const date = fileDescriptor.dateToSort ? fileDescriptor.dateToSort : fileDescriptor.registrationDate;
                if (bubble) {
                    conferenceRecordingFile = BubbleConferenceRecapRB.createFromData({
                        id: conferenceRecordingFileId,
                        recordingStartDate: date,
                        roomId: bubble.dbId,
                        roomName: bubble.name,
                        roomTopic: bubble.description,
                        records: []
                    });
                    conferenceRecordingFiles.push(conferenceRecordingFile);
                }
            }
            const recordData: BubbleConferenceRecapFile = {
                fileId: fileDescriptor.id,
                fileName: fileDescriptor.fileName,
                size: fileDescriptor.size,
                typeMIME: fileDescriptor.typeMIME
            };
            conferenceRecordingFile?.records.push(BubbleConferenceRecapFile.createFromData(recordData));

            return conferenceRecordingFiles;
        }, []);
    }

    public async getConferenceRecordingAssociatedFiles(conferenceRecordingFile: BubbleConferenceRecap): Promise<FileDescriptor[]> {
        try {
            if (!conferenceRecordingFile) throw new Error("[BubbleConferenceRecapService] getConferenceRecordingAssociatedFiles - ERROR: no conferenceRecordingFile");
            this.logger.info("[BubbleConferenceRecapService] getConferenceRecordingAssociatedFiles");
            return await this.fileStorageService.getConferenceRecordingAssociatedFiles(conferenceRecordingFile);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'stopRecording');
            this.logger.error(`[BubbleConferenceRecapService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public isMyRecord(BubbleConference: any): boolean { return BubbleConference.recordingUserId === this.contactService.userContact.dbId; }

    public async shareConferenceRecording(recap: BubbleConferenceRecap, destination: Bubble | User): Promise<void> {
        try {
            if (!recap) throw new Error("[BubbleConferenceRecapService] shareConferenceRecording - error: missing recap");
            if (!destination) throw new Error("[BubbleConferenceRecapService] shareConferenceRecording - error: missing destination");

            const dbId = destination.dbId;
            let type = 'user';
            if (destination.jid) { type = (destination.jid.indexOf("room_") !== -1) ? 'room' : 'user'; }
            await this.fileStorageService.addViewerToConferenceRecording(recap, dbId, type);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'shareConferenceRecording');
            this.logger.error(`[BubbleConferenceRecapService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async unshareConferenceRecording(recap: BubbleConferenceRecap, viewer: RecapViewer): Promise<void> {
        try {
            if (!recap) throw new Error("[BubbleConferenceRecapService] unshareConferenceRecording - error: missing recap");
            if (!viewer) throw new Error("[BubbleConferenceRecapService] unshareConferenceRecording - error: missing RecapViewer");
            const dbId = viewer.type === 'bubble' ? viewer.bubble?.dbId : viewer.user.dbId;
            await this.fileStorageService.removeViewerToConferenceRecording(recap, dbId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'unshareConferenceRecording');
            this.logger.error(`[BubbleConferenceRecapService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getCustomPromptsForTranscription(): Promise<{ name: string; id: string; }[]> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/aihub/v1.0/enduser/prompts`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const jsonResponse = await response.json();

            const items: { name: string; id: string; }[] = jsonResponse.data.map((item) => {
                return { name: item.name, id: item.id };
            });
            return items;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getCustomPromptsForTranscription");
            this.logger.error(`[BubbleConferenceRecapService] ${errorInfo.error.message}`);
            throw (errorInfo.error);
        }
    }
}
